import type { EventSource, SnowpadEvent } from "@/lib/analytics-events";
import { attributionToParams, getAttribution } from "@/lib/attribution";
import { sendGAEvent } from "@next/third-parties/google";

const SESSION_KEY = "snowpad_session_id";
const GA_CLIENT_ID_KEY = "snowpad_ga_client_id";

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = `s_${crypto.randomUUID()}`;
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Try to capture GA4's client_id so Loki events can be joined with GA4 sessions.
 * Falls back to cached value; non-blocking best effort.
 */
function getOrFetchGAClientId(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const cached = window.sessionStorage.getItem(GA_CLIENT_ID_KEY);
    if (cached) return cached;

    const gtag = (window as unknown as Record<string, unknown>).gtag;
    if (typeof gtag === "function") {
      const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
      if (measurementId) {
        gtag("get", measurementId, "client_id", (clientId: string) => {
          try {
            window.sessionStorage.setItem(GA_CLIENT_ID_KEY, clientId);
          } catch {
            /* ignore storage errors */
          }
        });
      }
    }
  } catch {
    /* ignore gtag errors */
  }

  return null;
}

export type TrackParams = Record<string, string | number | boolean>;

export function track(
  event: SnowpadEvent,
  source: EventSource,
  params?: TrackParams,
  route?: string,
): void {
  if (typeof window === "undefined") return;

  const sessionId = getOrCreateSessionId();
  const gaClientId = getOrFetchGAClientId();
  const attributionParams = attributionToParams(getAttribution());

  const payload: Record<string, unknown> = {
    event,
    source,
    params: { ...attributionParams, session_id: sessionId, ...(params ?? {}) },
    route: route ?? window.location.pathname,
  };

  if (gaClientId) {
    payload.ga_client_id = gaClientId;
  }

  const body = JSON.stringify(payload);

  // --- Send to internal Loki analytics ---
  try {
    const blob = new Blob([body], { type: "application/json" });
    const ok = navigator.sendBeacon?.("/api/track", blob);
    if (ok) return;
  } catch {
  }

  try {
    void fetch("/api/track", {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body,
    });
  } catch {
  }

  // --- Mirror to GA4 for attribution visibility ---
  try {
    sendGAEvent("event", event, {
      event_category: source,
      snowpad_session_id: sessionId,
      ...attributionParams,
      ...params,
    });
  } catch {
    // Silently fail if GA4 hasn't loaded yet
  }
}
